import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

export interface TourTooltipContentProps {
  /** Main text shown in the tooltip */
  title: string;
  /** Optional body text below the title */
  body?: string;
  onNext: () => void;
  onSkip: () => void;
  /** If true, primary button shows "Finish" instead of "Next" */
  isLastStep?: boolean;
  /** When set, show only this action (no Finish/Next or Skip Tour). Used e.g. for step 3 "Start" → go to Color Analysis */
  startActionLabel?: string;
  onStartAction?: () => void;
}

const TourTooltipContent: React.FC<TourTooltipContentProps> = ({
  title,
  body,
  onNext,
  onSkip,
  isLastStep = false,
  startActionLabel,
  onStartAction,
}) => {
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();
  const showStartOnly = startActionLabel != null && onStartAction != null;

  return (
    <View
      style={[
        styles.content,
        isMobile && styles.contentMobile,
        { backgroundColor: colors.card },
      ]}
    >
      <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
      {body ? (
        <Text
          style={[
            styles.body,
            isMobile && styles.bodyMobile,
            { color: colors.secondary },
          ]}
        >
          {body}
        </Text>
      ) : null}
      <View style={styles.actions}>
        {showStartOnly ? (
          <Pressable
            onPress={onStartAction}
            style={({ pressed }) => [pressed && styles.linkPressed]}
            hitSlop={8}
          >
            <Text style={[styles.linkText, { color: colors.primary }]}>
              {startActionLabel}
            </Text>
          </Pressable>
        ) : (
          <>
            <Pressable
              onPress={onSkip}
              style={({ pressed }) => [pressed && styles.linkPressed]}
              hitSlop={8}
            >
              <Text style={[styles.linkText, { color: colors.primary }]}>
                Skip Tour
              </Text>
            </Pressable>
            <Pressable
              onPress={onNext}
              style={({ pressed }) => [pressed && styles.linkPressed]}
              hitSlop={8}
            >
              <Text style={[styles.linkText, { color: colors.primary }]}>
                {isLastStep ? 'Finish' : 'Next'}
              </Text>
            </Pressable>
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    minWidth: 220,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    }),
  },
  contentMobile: {
    minWidth: 0,
    width: '100%',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 6,
    flexShrink: 1,
  },
  body: {
    fontSize: 14,
    marginBottom: 14,
    lineHeight: 20,
  },
  bodyMobile: {
    flexShrink: 1,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
  },
  linkText: {
    fontSize: 14,
    fontWeight: '600',
  },
  linkPressed: {
    opacity: 0.7,
  },
});

export default TourTooltipContent;
